'use client';

import { PathEnum } from '@/i18n/locale-config';
import { postData } from '@/utils/api-helpers';
import { handleFormSubmitApiError } from '@/utils/form-submit-error';
import { cn } from '@/utils/tailwind-merge';
import { signupSchema } from '@/utils/validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import RegisterSuccess from '../blocks/RegisterSuccess';
import Button from '../ui/Button';
import ErrorText from '../ui/ErrorText';
import Input from '../ui/Input';
import Link from '../ui/Link';

type FormData = z.infer<typeof signupSchema>;

export default function SignupForm({ className }: { className?: string }) {
    const t = useTranslations();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(signupSchema),
    });

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);
        try {
            const response = await postData('/auth/register', formData);

            if (response.statusCode !== 201) {
                throw new Error(response.message || 'An error occurred');
            }

            reset();
            setIsSuccessful(true);
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    return isSuccessful ? (
        <div
            className={cn(
                'w-full flex flex-col gap-7 justify-center items-center relative',
                className,
            )}
        >
            <RegisterSuccess className="mt-20 w-full" />
        </div>
    ) : (
        <form
            className={cn(
                'flex flex-col gap-7 justify-center items-center relative text-white',
                className,
            )}
            onSubmit={handleSubmit(onSubmit)}
        >
            <h4 className="text-h4 lg:text-h1 uppercase">{t('Form.signUp')}</h4>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-w-[940px] w-full">
                <Input
                    {...register('firstName')}
                    className="newsletter-input"
                    type="text"
                    name="firstName"
                    id="firstName"
                    placeholder={t('Form.firstName')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('firstName')}
                    required
                />
                <Input
                    {...register('lastName')}
                    className="newsletter-input"
                    type="text"
                    name="lastName"
                    id="lastName"
                    placeholder={t('Form.lastName')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('lastName')}
                    required
                />
                <Input
                    {...register('email')}
                    className="newsletter-input"
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.email')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('email')}
                    required
                />
                <Input
                    {...register('phone')}
                    className="newsletter-input"
                    type="text"
                    name="phone"
                    id="phone"
                    placeholder={t('Form.phone')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('phone')}
                />
                <Input
                    {...register('password')}
                    className="newsletter-input"
                    type="password"
                    name="password"
                    id="password"
                    placeholder={t('Form.password')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('password')}
                    required
                />
                <Input
                    {...register('confirmPassword')}
                    className="newsletter-input"
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    placeholder={t('Form.confirmPassword')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('confirmPassword')}
                    required
                />
            </div>
            {errors && (
                <div className="flex flex-col text-center">
                    <ErrorText error={getFieldError('phone')} />
                    <ErrorText error={getFieldError('email')} />
                    <ErrorText error={getFieldError('confirmPassword')} />
                </div>
            )}
            <Button
                className="w-full max-w-[460px]"
                textClasses="text-white"
                type="submit"
                variant="secondary"
                title={t('Form.signUp')}
                submitting={submitting}
                disabled={submitting}
            />
            <p className="mt-7 text-xl font-helvetica-neue font-light leading-6 text-center">
                {t('Common.alreadyHaveAccount')}{' '}
                <Link href={PathEnum.SignIn} className="text-primary" title={t('Form.signIn')} />
            </p>
        </form>
    );
}
